// @flow
import * as React from 'react';
import Modal from 'react-modal';
import pluralize from 'pluralize';
import invariant from 'invariant';

import {
  ComposableTable,
  getErrorCellGenerator,
  // getTextCellGenerator,
  getTimestampCellGenerator,
} from 'components/ComposableTable';
import { AdminUploadedFileDetailModal } from 'admin/components/AdminUploadedFileDetail';
import BoxBody from 'components/BoxBody';
import DeliveryModalSectionItem from 'chat/components/DeliveryModalSection/DeliveryModalSectionItem';
import Dialog from 'components/Dialog';
import Heading from 'components/Heading';
import Row from 'components/Row';
import ScrollableArea from 'components/ScrollableArea';
import Text from 'components/Text';

import type {
  GetAutoMessageByUuid_autoMessaging_autoMessages_autoMessages_unsentFileUploads as UnsentFileUpload,
  GetAutoMessageByUuid_autoMessaging_autoMessages_autoMessages_unsentFileUploads_identificationErrorEntry as IdentificationErrorEntry,
  GetAutoMessageByUuid_autoMessaging_autoMessages_autoMessages_unsentFileUploads_identificationErrorEntry_PreciseSummaryEntry_unidentifiable as IdentificationErrorEntryUnidentifiables,
  GetAutoMessageByUuid_autoMessaging_autoMessages_autoMessages_unsentFileUploads_identificationErrorEntry_PreciseSummaryEntry_users as IdentificationErrorEntryUsers,
} from 'admin/components/AdminAutoMessages/queries/__generated__/GetAutoMessageByUuid';

type Props = {
  unsentFileUploads: $ReadOnlyArray<UnsentFileUpload>,
  csvName: string,
};

type State = {
  uploadedFileForModal: ?*,
  uploadedFileModalIsOpen: boolean,
  errorDetailsModalIsOpen: boolean,
  errorDetailsIdentificationErrorEntry: ?IdentificationErrorEntry,
};

export default class AdminAutoMessageDetailUnsentFilesTable extends React.Component<Props, State> {
  state = {
    uploadedFileModalIsOpen: false,
    uploadedFileForModal: undefined,
    errorDetailsModalIsOpen: false,
    errorDetailsIdentificationErrorEntry: undefined,
  };

  showDetailsModal = (errorDetailsIdentificationErrorEntry: IdentificationErrorEntry) => {
    this.setState({ errorDetailsIdentificationErrorEntry }, () =>
      this.setState({ errorDetailsModalIsOpen: true })
    );
  };

  hideDetailsModal = () => {
    this.setState({ errorDetailsIdentificationErrorEntry: undefined }, () =>
      this.setState({ errorDetailsModalIsOpen: true })
    );
  };

  showUploadedFileModal = (uploadedFile: *) => {
    this.setState({ uploadedFileForModal: uploadedFile, uploadedFileModalIsOpen: true });
  };

  hideUploadedFileModal = () => {
    this.setState({ uploadedFileForModal: undefined, uploadedFileModalIsOpen: false });
  };

  formatDataToRow = (unsentFile: *) => {
    const { uploadedAt, uuid, identificationErrorEntry, errors } = unsentFile;
    invariant(
      identificationErrorEntry,
      'AdminAutoMessageDetailUnsentFilesTable - identificationErrorEntry cannot be undefined'
    );
    invariant(
      identificationErrorEntry.__typename === 'PreciseSummaryEntry',
      'AdminAutoMessageDetailUnsentFilesTable - identificationErrorEntry cannot be estimated'
    );
    const csvErrors = errors ? errors.length : 0;
    const recipientErrors = csvErrors ? null : identificationErrorEntry.count;
    const fields = {
      uuid,
      uploadedAt,
      csvErrors,
      recipientErrors,
      identificationErrorEntry,
    };

    if (csvErrors > 0) {
      return {
        ...fields,
        onClick: () => this.showUploadedFileModal(unsentFile),
      };
    }

    if (identificationErrorEntry.count > 0) {
      return {
        ...fields,
        onClick: () => this.showDetailsModal(identificationErrorEntry),
      };
    }

    return fields;
  };

  renderUnidentifiableStudentSection = (
    unidentifiable: $ReadOnlyArray<IdentificationErrorEntryUnidentifiables>
  ) => {
    if (unidentifiable.length > 0) {
      return (
        <React.Fragment>
          <Heading level="3">Unidentified students</Heading>
          <Text color="thunder" size="xsmall">
            There are no students with the following student numbers in your organization on Remind.
          </Text>
          {unidentifiable.map((unid, index) => (
            <DeliveryModalSectionItem
              key={unid.studentId || index}
              user={undefined}
              avatarLabel={unid.studentId || 'Unidentifiable'}
              deliveryError={undefined}
            />
          ))}
        </React.Fragment>
      );
    }
    return null;
  };

  renderMissingParentSection = (users: $ReadOnlyArray<IdentificationErrorEntryUsers>) => {
    if (users.length > 0) {
      return (
        <React.Fragment>
          <Heading level="3">Missing parents</Heading>
          <Text color="thunder" size="xsmall">
            There are no parents associated with the following students in your organization on
            Remind.
          </Text>
          {users.map(user => (
            <DeliveryModalSectionItem
              key={user.uuid}
              user={user}
              avatarLabel={user.name}
              deliveryError={undefined}
            />
          ))}
        </React.Fragment>
      );
    }
    return null;
  };

  renderErrorDetailsModal = () => {
    const { errorDetailsIdentificationErrorEntry, errorDetailsModalIsOpen } = this.state;

    if (errorDetailsIdentificationErrorEntry) {
      invariant(
        errorDetailsIdentificationErrorEntry.__typename === 'PreciseSummaryEntry',
        'AdminAutoMessageDetailUnsentFilesTable - errorDetailsIdentificationErrorEntry cannot be estimated'
      );
      return (
        <Modal isOpen={errorDetailsModalIsOpen} data-test="error-details-modal">
          <Dialog
            onRequestHide={this.hideDetailsModal}
            size="small"
            textAlign="left"
            title="Recipient error summary"
            subtext={`${errorDetailsIdentificationErrorEntry.count} ${pluralize(
              'people',
              errorDetailsIdentificationErrorEntry.count
            )} from ${this.props.csvName} would not have received this message`}>
            <BoxBody divided>
              <ScrollableArea maxHeight={300}>
                <Text align="left">
                  {this.renderUnidentifiableStudentSection(
                    errorDetailsIdentificationErrorEntry.unidentifiable
                  )}
                  {this.renderMissingParentSection(errorDetailsIdentificationErrorEntry.users)}
                </Text>
              </ScrollableArea>
            </BoxBody>
          </Dialog>
        </Modal>
      );
    }
    return null;
  };

  renderUploadedFileModal = () => {
    const { uploadedFileModalIsOpen, uploadedFileForModal } = this.state;
    if (uploadedFileForModal) {
      return (
        <Modal isOpen={uploadedFileModalIsOpen} data-test="file-details-modal">
          <AdminUploadedFileDetailModal
            onClose={this.hideUploadedFileModal}
            fileUpload={uploadedFileForModal}
          />
        </Modal>
      );
    }
    return null;
  };

  render() {
    const { unsentFileUploads } = this.props;

    const columnHeaders = [
      { name: 'CSV transfer time' },
      { name: 'CSV errors' },
      // { name: 'Selected recipients' },
      { name: 'Recipient errors' },
    ];

    const columnGenerators = [
      getTimestampCellGenerator('uploadedAt'),
      getErrorCellGenerator('csvErrors'),
      // getTextCellGenerator('recipientCount'),
      getErrorCellGenerator('recipientErrors'),
    ];

    // $FlowFixMe
    const rows = unsentFileUploads.map(unsentFile => this.formatDataToRow(unsentFile));
    return (
      <React.Fragment>
        {this.renderErrorDetailsModal()}
        {this.renderUploadedFileModal()}
        <Row size="small">
          <Text size="small" lineHeight="large">
            These messages were not sent. To see the recipient errors that would have occurred for a
            particular upload, click on the corresponding row below.
          </Text>
        </Row>
        <ComposableTable
          data-test="unsent-uploads-table"
          columnHeaders={columnHeaders}
          columnGenerators={columnGenerators}
          rows={rows}
          keyField="uuid"
        />
      </React.Fragment>
    );
  }
}
